import { useState, useEffect, useCallback } from "react";
import {
  LayoutDashboard, Trash2, CheckCircle, ScrollText, BarChart3,
  TrendingUp, Award
} from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import { useAuth } from "../../hooks/useAuth";
import { supabase } from "../../lib/supabaseClient";

const NAV = [
  { label: "Dashboard",     icon: LayoutDashboard, path: "/hoa/dashboard",    active: true },
  { label: "Submissions",   icon: Trash2,           path: "/hoa/submissions" },
  { label: "Valet Status",  icon: CheckCircle,      path: "/hoa/valet-status" },
  { label: "Activity Logs", icon: ScrollText,       path: "/hoa/logs" },
  { label: "Reports",       icon: BarChart3,        path: "/hoa/reports" },
];


function getToday() { return new Date().toISOString().split("T")[0]; }

function getLast7Days() {
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push(d.toISOString().split("T")[0]);
  }
  return days;
}

function getLast30Days() {
  const days = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push(d.toISOString().split("T")[0]);
  }
  return days;
}

function formatShortDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function StatCard({ label, value, sub, color = "var(--g600)", bg = "var(--g50)", border = "var(--g200)", icon: Icon, delay }) {
  return (
    <div className={`fu ${delay}`} style={{ background: bg, border: `1.5px solid ${border}`, borderRadius: 13, padding: "16px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", color: "var(--mut)" }}>{label}</div>
        {Icon && <div style={{ width: 30, height: 30, borderRadius: 8, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon size={14} color={color} /></div>}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color, fontFamily: "'Outfit',sans-serif", lineHeight: 1 }}>{value ?? "—"}</div>
      {sub && <div style={{ fontSize: 12, color: "var(--mut)", marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

// ─── Bar Chart ────────────────────────────────────────────────────────────────
function BarChart({ data, maxVal, color = "var(--g600)" }) {
  if (!data.length) return null;
  const max = maxVal || Math.max(...data.map(d => d.value), 1);

  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 80 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <div style={{ fontSize: 9, color: "var(--mut)", fontWeight: 600 }}>{d.value > 0 ? d.value : ""}</div>
          <div style={{
            width: "100%", borderRadius: "4px 4px 0 0",
            background: d.value > 0 ? color : "#f0f0f0",
            height: `${Math.max(4, (d.value / max) * 56)}px`,
            transition: "height .3s ease",
            opacity: d.isToday ? 1 : 0.7,
          }} />
          <div style={{ fontSize: 9, color: "var(--mut)", textAlign: "center", lineHeight: 1.2 }}>{d.label}</div>
        </div>
      ))}
    </div>
  );
}

export default function HOAReports() {
  const { profile } = useAuth();
  const propertyId  = profile?.property_id;
  const today       = getToday();

  const [range,       setRange]       = useState("7");
  const [loading,     setLoading]     = useState(true);
  const [totalUnits,  setTotalUnits]  = useState(0);
  const [dailyData,   setDailyData]   = useState([]);
  const [summary,     setSummary]     = useState({ totalSubs: 0, totalServices: 0, avgPerDay: 0, bestDay: null, bestCount: 0 });

  useEffect(() => {
    if (!propertyId) return;
    supabase.from("properties").select("total_units").eq("id", propertyId).maybeSingle()
      .then(({ data }) => setTotalUnits(data?.total_units || 0));
  }, [propertyId]);

  const fetchReports = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);

    const days     = range === "7" ? getLast7Days() : getLast30Days();
    const dateFrom = days[0];
    const dateTo   = days[days.length - 1];

    // Fetch submissions
    const { data: subs } = await supabase
      .from("activity_logs")
      .select("created_at")
      .eq("property_id", propertyId)
      .eq("action", "resident_trash_submitted")
      .gte("created_at", `${dateFrom}T00:00:00`)
      .lte("created_at", `${dateTo}T23:59:59`);

    // Fetch completed services
    const { data: services } = await supabase
      .from("valet_status")
      .select("service_date")
      .eq("property_id", propertyId)
      .eq("status", "completed")
      .gte("service_date", dateFrom)
      .lte("service_date", dateTo);

    // Build daily map
    const subsByDay     = {};
    const servicesByDay = {};
    days.forEach(d => { subsByDay[d] = 0; servicesByDay[d] = 0; });
    (subs || []).forEach(s => {
      const d = s.created_at.split("T")[0];
      if (subsByDay[d] !== undefined) subsByDay[d]++;
    });
    (services || []).forEach(s => {
      if (servicesByDay[s.service_date] !== undefined) servicesByDay[s.service_date]++;
    });

    // Build chart data
    const chartData = days.map(d => ({
      label:   formatShortDate(d).replace(/\d{4}/, "").trim(),
      value:   subsByDay[d],
      isToday: d === today,
    }));
    setDailyData(chartData);

    // Summary
    const totalSubs     = Object.values(subsByDay).reduce((a, b) => a + b, 0);
    const totalServices = Object.values(servicesByDay).reduce((a, b) => a + b, 0);
    const activeDays    = days.filter(d => subsByDay[d] > 0).length;
    const avgPerDay     = activeDays > 0 ? Math.round(totalSubs / activeDays) : 0;
    const bestDayKey    = Object.entries(subsByDay).sort((a, b) => b[1] - a[1])[0];

    setSummary({
      totalSubs,
      totalServices,
      avgPerDay,
      bestDay:   bestDayKey?.[1] > 0 ? formatShortDate(bestDayKey[0]) : null,
      bestCount: bestDayKey?.[1] || 0,
    });

    setLoading(false);
  }, [propertyId, range, today]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const avgPct = totalUnits > 0 ? Math.round((summary.avgPerDay / totalUnits) * 100) : 0;

  return (
    <DashboardLayout role="hoa_admin" title="Reports" navItems={NAV}>

      {/* Header */}
      <div className="fu" style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "#1565c0", marginBottom: 6 }}>HOA Admin</div>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 500, color: "var(--ink)", margin: 0 }}>Reports</h2>
            <p style={{ fontSize: 13, color: "var(--mut)", marginTop: 4 }}>Submission trends and service performance.</p>
          </div>
          {/* Range Toggle */}
          <div style={{ display: "flex", background: "#f5f5f5", borderRadius: 10, padding: 3, gap: 3 }}>
            {[{ key: "7", label: "Last 7 Days" }, { key: "30", label: "Last 30 Days" }].map(({ key, label }) => (
              <button key={key} onClick={() => setRange(key)} style={{
                padding: "7px 14px", borderRadius: 8, border: "none",
                background: range === key ? "#fff" : "transparent",
                color: range === key ? "var(--ink)" : "var(--mut)",
                fontSize: 12, fontWeight: 700, cursor: "pointer",
                boxShadow: range === key ? "0 1px 4px rgba(0,0,0,.08)" : "none",
                transition: "all .15s",
              }}>{label}</button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: "var(--mut)", fontSize: 13 }}>Loading reports…</div>
      ) : (
        <>
          {/* Summary Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 24 }}>
            <StatCard label="Total Submissions" value={summary.totalSubs}    sub={`Last ${range} days`}   color="var(--g600)" bg="var(--g50)"  border="var(--g200)" icon={Trash2}     delay="d1" />
            <StatCard label="Services Completed" value={summary.totalServices} sub={`Last ${range} days`} color="#1565c0"     bg="#e3f2fd"    border="#90caf9"     icon={CheckCircle} delay="d2" />
            <StatCard label="Avg Per Active Day"  value={summary.avgPerDay}   sub={`${avgPct}% of units`} color="#e65100"     bg="#fff3e0"    border="#ffb74d"     icon={TrendingUp}  delay="d3" />
            <StatCard label="Best Day"            value={summary.bestDay || "—"} sub={summary.bestDay ? `${summary.bestCount} submissions` : "No data"} color="#6a1b9a" bg="#f3e5f5" border="#ce93d8" icon={Award} delay="d4" />
          </div>

          {/* Bar Chart */}
          <div className="fu d3" style={{ background: "#fff", borderRadius: 14, border: "1px solid var(--ln)", padding: "20px", marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "var(--mut)", marginBottom: 16 }}>
              Daily Submissions — Last {range} Days
            </div>
            {dailyData.every(d => d.value === 0) ? (
              <div style={{ textAlign: "center", padding: "24px 0", fontSize: 13, color: "var(--mut)" }}>
                No submission data for this period.
              </div>
            ) : (
              <BarChart data={dailyData} color="var(--g600)" />
            )}
          </div>

          {/* Participation Rate */}
          <div className="fu d4" style={{ background: "#fff", borderRadius: 14, border: "1px solid var(--ln)", padding: "20px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "var(--mut)", marginBottom: 14 }}>
              Avg Daily Participation Rate
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ height: 10, borderRadius: 99, background: "#f0f0f0", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${avgPct}%`, background: "var(--g600)", borderRadius: 99, transition: "width .4s ease" }} />
                </div>
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "var(--g600)", fontFamily: "'Outfit',sans-serif", flexShrink: 0 }}>
                {avgPct}%
              </div>
            </div>
            <div style={{ fontSize: 12, color: "var(--mut)", marginTop: 8 }}>
              {summary.avgPerDay} avg submissions per active day · {totalUnits} total units
            </div>
          </div>
        </>
      )}

      <style>{`@keyframes fadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </DashboardLayout>
  );
}